<template>
  <div class="themecard-teaser">
    <div class="themecard-img ratio ratio-1x1">
      <div v-if="imgSrc" :style="`background-image: url(${imgSrc});`" @click="onClick">
        <span class="sr-only">{{ imgAlt }}</span>
      </div>

      <c-placeholder-image v-else size="75"/>
    </div>

    <div class="themecard-content">
      <div class="themecard-header">
        <slot name="header"/>
      </div>

      <div class="themecard-body">
        <c-carousel
          class="themecard-products"
          :autoplay="false"
          :adaptiveHeight="false"
          :slidesToShow="4"
          :slidesToScroll="4"
          :responsive="[
            {
              breakpoint: BREAKPOINTS.md,
              settings: {
                slidesToShow: 3,
                slidesToScroll: 3
              }
            },
            {
              breakpoint: BREAKPOINTS.sm,
              settings: {
                adaptiveHeight: true,
                slidesToShow: 2,
                slidesToScroll: 2
              }
            }
          ]"
        >
          <slot/>
        </c-carousel>

        <div v-if="$slots.productcount" class="themecard-product-count">
          <small><slot name="productcount"/></small>
        </div>
      </div>

      <div class="themecard-footer">
        <a class="themecard-actionbutton btn btn-document btn-sm" :href="href" target="_blank" ref="link">
          <slot name="actionbutton"/>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { BREAKPOINTS } from '@/assets/js/modules/scss-variables'

export default {
  name: 'ThemecardTeaser',
  props: {
    href: {
      type: String,
      required: true
    },
    imgSrc: {
      type: String,
      default: null
    },
    imgAlt: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      BREAKPOINTS
    }
  },
  methods: {
    onClick (e) {
      this.$refs.link.dispatchEvent(new MouseEvent('click', { ctrlKey: e.ctrlKey }))
    }
  }
}
</script>

<style lang="scss">
$themecard-teaser-gap: $grid-gutter-width !default;
$themecard-teaser-padding-y: $component-padding-y * 0.6 !default;
$themecard-teaser-padding-x: $component-padding-x !default;
$themecard-teaser-bg: $card-bg !default;
$themecard-teaser-font-size: $font-size-base * 0.875 !default;

$themecard-teaser-img-width: 31% !default;
$themecard-teaser-img-transition-in: transform ease-out 200ms !default;
$themecard-teaser-img-transition-out: transform ease-out 200ms !default;

$themecard-teaser-product-padding-y: 0 !default;
$themecard-teaser-product-padding-x: $spacer * 0.5 !default;
$themecard-teaser-product-font-size: $font-size-base * 0.75 !default;
$themecard-teaser-product-any-gap: $spacer * 0.5 !default;

$themecard-teaser-product-dots-gap: $spacer * 0.5 !default;

$themecard-teaser-actionbutton-animation: h-bounce 400ms ease-out 1 !default;

$themecard-teaser-img-lg-width: 34% !default;

$themecard-teaser-img-md-width: 36% !default;

$themecard-teaser-img-sm-width: 100% !default;

.themecard-teaser {
  @include make-leaf($leaf-border-radius-lg);
  display: flex;
  position: relative;
  overflow: hidden;
  margin-bottom: $themecard-teaser-gap;
  background-color: $themecard-teaser-bg;
  font-size: $themecard-teaser-font-size;

  &:last-child {
    margin-bottom: 0;
  }

  .themecard-img {
    @include make-leaf($leaf-border-radius-lg, 'top');
    width: $themecard-teaser-img-width;
    cursor: pointer;

    div {
      background: no-repeat center center;
      background-size: cover;
      transform-origin: center right;
      transform: scale(1);
      transition: $themecard-teaser-img-transition-out;
    }
  }

  .themecard-content {
    flex: 1 0 0%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;

    .themecard-header {
      overflow: hidden;
      padding: $themecard-teaser-padding-y $themecard-teaser-padding-x 0;
    }

    .themecard-body {
      padding: ($themecard-teaser-padding-y * 0.5) $themecard-teaser-padding-x;
    }

    .themecard-footer {
      padding: 0 $themecard-teaser-padding-x $themecard-teaser-padding-y;
    }

    .themecard-header,
    .themecard-body,
    .themecard-footer {
      @include clearfix();

      > * {
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .themecard-title {}

  .themecard-products {
    margin: 0;

    .slick-list {
      @include reset-leaf();
    }

    .slick-slide {
      [tabindex="-1"] {
        padding: $themecard-teaser-product-padding-y $themecard-teaser-product-padding-x;
        font-size: $themecard-teaser-product-font-size;

        * {
          + * {
            margin-top: $themecard-teaser-product-any-gap;
          }
        }

        a {
          display: block;
        }

        p {
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: center;
          white-space: nowrap;
        }
      }
    }

    .slick-dots {
      margin-top: $themecard-teaser-product-dots-gap;
      margin-bottom: 0;
    }
  }

  .themecard-product-count {
    line-height: 1;
  }

  .themecard-actionbutton {
    display: block;
    float: right;
  }

  &:hover {
    .themecard-img {
      > div {
        transform: scale(1.05);
        transition: $themecard-teaser-img-transition-in;
      }
    }

    .themecard-actionbutton {
      animation: $themecard-teaser-actionbutton-animation;
    }
  }

  @include media-breakpoint-down(lg) {
    .themecard-img {
      width: $themecard-teaser-img-lg-width;
    }
  }

  @include media-breakpoint-down(md) {
    .themecard-img {
      width: $themecard-teaser-img-md-width;
    }
  }

  @include media-breakpoint-down(sm) {
    flex-direction: column;

    .themecard-img {
      width: $themecard-teaser-img-sm-width;
      transform-origin: bottom center;
    }

    .themecard-content {
      .themecard-body {
        padding-top: $themecard-teaser-padding-y;
        padding-bottom: $themecard-teaser-padding-y;
      }
    }

    .themecard-products {
      .slick-slide {
        [tabindex="-1"] {
          p {
            overflow: visible;
            white-space: normal;
          }
        }
      }
    }
  }
}
</style>
